import { z } from "zod";

import { consoleFor } from "../../rest/rules/console";
import type { RestClientConfig } from "../../rest/rules/sdk";
import type { components } from "../../schema/api";
import {
  itineraryLimitSchema,
  studioEntitlementsSchema,
  userOrganizationsQueryKey,
  type WithWireFeatures,
} from "./organizations";

/**
 * PersonalSpace is what Mountaya answers the signed-in user may use of Map Studio in their
 * personal space, and how many itineraries they may keep there: the personal space's
 * counterpart of the answer every membership carries. It is the console's type (no key reaches
 * the personal space, so the public contract publishes no read of it), picked from the generated
 * one so a renamed field fails to compile, with the features read as the wire spells them, as a
 * membership's are ({@link studioEntitlementsSchema}).
 */
export type PersonalSpace = WithWireFeatures<
  Pick<components["schemas"]["PersonalSpace"], "itinerary_limit" | "studio_entitlements">
>;

/**
 * personalSpaceSchema parses the answer at the boundary, as leniently as a membership's: an
 * answer missing its features or its ceiling reads as offering nothing and stating no ceiling.
 * The server refuses whatever the answer does not offer, so a lenient read only ever withholds an
 * affordance.
 */
const personalSpaceSchema = z.object({
  itinerary_limit: itineraryLimitSchema,
  studio_entitlements: studioEntitlementsSchema,
}) satisfies z.ZodType<PersonalSpace>;

/** personalSpaceResponseSchema is the REST envelope around the personal space's answer. */
const personalSpaceResponseSchema = z.object({
  data: personalSpaceSchema,
});

/**
 * userPersonalSpaceQueryKey keys the personal space's answer for a user under that user's
 * memberships. What the personal space offers follows them (its insights follow belonging to an
 * organization holding Map Studio), so a write that invalidates the memberships, an invitation
 * answered or an organization created, refreshes this answer with them.
 */
export const userPersonalSpaceQueryKey = (userId: string) =>
  [...userOrganizationsQueryKey, userId, "personal-space"] as const;

/**
 * fetchUserPersonalSpace is a hookless query-fn: it reads `getMyPersonalSpace`, the personal
 * space of the user the console session belongs to, through the console client (the operation is
 * console-only: no key reaches the personal space). The session names the user, so the request
 * names nobody. A non-ok response throws the transport's typed error, so TanStack Query drives
 * its error state.
 */
export const fetchUserPersonalSpace = async (config: RestClientConfig): Promise<PersonalSpace> => {
  const body: unknown = await consoleFor(config).admin.users.getMyPersonalSpace();

  return personalSpaceResponseSchema.parse(body).data;
};

/**
 * userPersonalSpaceQueryOptions bundles the query key and the hookless fetch fn, keyed per user,
 * for useQuery and a render-free caller alike. The user is in the key although the request does
 * not name them, for the reason the memberships' key carries it.
 */
export const userPersonalSpaceQueryOptions = (config: RestClientConfig, userId: string) => ({
  queryFn: () => fetchUserPersonalSpace(config),
  queryKey: userPersonalSpaceQueryKey(userId),
});
